'use client';
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Box } from '@mui/material';

import { ComponentErrorBoundary, FeaturedMarketsCarousel, TradeModal } from '@/components';
import { useOpenAuthModal } from '@/hooks';
import type { UseHomeReturn } from '@/hooks/Home/useHome';
import { useMixpanel } from '@/hooks/useMixpanel';
import { useAuth } from '@/store/hooks';
import { HomeContent, SearchAndFilters } from './subcomponents';

import { ROUTES } from '@/constants';

interface HomeComponentProps {
  homeData: UseHomeReturn;
}

/**
 * HomeComponent
 *
 * The main presentational component for the Home page.
 * It manages the layout and integrates various subcomponents like navigation,
 * search/filters, and the main content area (markets list).
 *
 * It relies on the `useHome` hook for logic and state management.
 */
export const HomeComponent = ({ homeData }: HomeComponentProps) => {
  const router = useRouter();
  const openAuthModal = useOpenAuthModal();
  const { isAuthenticated } = useAuth();

  // Destructure state, handlers, and refs from the useHome hook result
  const { state, handlers } = homeData;
  const {
    activeCategorySlug,
    tradeModalState,
    searchQuery,
    isFilterOpen,
    sortBy,
    frequency,
    status,
    events,
    isLoading,
    error,
    tags,
    isTagsLoading,
    selectedTag,
    hideSport,
  } = state;

  const {
    setSortBy,
    setFrequency,
    setStatus,
    handleSearchChange,
    handleSearchSubmit,
    handleFilterToggle,
    handleTradeClick,
    handleTradeModalClose,
    handleTagClick,
    setHideSport,
    refetch,
  } = handlers;

  const { trackLandingPageViewed, trackMarketListViewed } = useMixpanel();

  useEffect(() => {
    trackLandingPageViewed({
      device_type: navigator.userAgent,
      referrer_url: document.referrer,
    });
    trackMarketListViewed({
      category_filter: activeCategorySlug || 'all',
      sort_by: sortBy,
      total_markets_shown: events?.length || 0,
      topic_tag: selectedTag || 'none',
    });
  }, []);

  // Navigate to the bookmarks page; prompt sign-in if unauthenticated
  const handleBookmarksClick = () => {
    if (!isAuthenticated) {
      openAuthModal();
      return;
    }
    router.push(ROUTES.BOOKMARKS);
  };

  const shouldShowTradeModal = !!(
    tradeModalState.isOpen &&
    tradeModalState.market &&
    tradeModalState.outcome
  );

  return (
    <Box
      sx={{
        py: 2,
      }}
    >
      {/* Sticky Filters Section */}
      <Box
        sx={{
          position: 'sticky',
          top: { xs: '96px', sm: '100px', md: '104px' },
          zIndex: 10,
          bgcolor: 'background.default',
          mx: { xs: -1.5, sm: -2, md: -3, lg: -4 },
          px: { xs: 1.5, sm: 2, md: 3, lg: 4 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 2,
        }}
      >
        {/* Search Bar and Filters Section */}
        <SearchAndFilters
          searchQuery={searchQuery}
          isFilterOpen={isFilterOpen}
          selectedTag={selectedTag}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          onFilterToggle={handleFilterToggle}
          onBookmarksOpen={handleBookmarksClick}
          tags={tags}
          isTagsLoading={isTagsLoading}
          onTagClick={handleTagClick}
          activeCategorySlug={activeCategorySlug}
          sortBy={sortBy}
          frequency={frequency}
          status={status}
          onSortByChange={setSortBy}
          onFrequencyChange={setFrequency}
          onStatusChange={setStatus}
          hideSport={hideSport}
          onHideSportChange={setHideSport}
          onClearFilters={handlers.handleResetFilters}
        />
      </Box>

      {/* Featured markets carousel — fed by the live grid events, hidden during search */}
      {!searchQuery && (
        <ComponentErrorBoundary componentName="Featured Markets">
          <FeaturedMarketsCarousel events={events} />
        </ComponentErrorBoundary>
      )}

      {/* Main Content Area (Loading, Error, or Market List) */}
      <HomeContent
        isLoading={isLoading}
        error={error}
        events={events}
        activeCategorySlug={activeCategorySlug}
        pagination={homeData.pagination}
        onTradeClick={handleTradeClick}
        onFetchNextPage={handlers.fetchNextPage}
        onRetry={refetch}
        isResolvedFilter={status === 'Resolved'}
      />

      {/* Trade Implementation Modal */}
      <TradeModal
        isOpen={shouldShowTradeModal}
        onClose={handleTradeModalClose}
        market={tradeModalState.market}
        outcome={tradeModalState.outcome}
        isBuyOrder={tradeModalState.isBuyOrder}
        forceDesktopVersion={tradeModalState.forceDesktopVersion}
      />
    </Box>
  );
};
